import React, { useRef } from 'react';
import { Box } from '@mui/system';
import isArray from 'lodash/isArray';
import throttle from 'lodash/throttle';
import { Button } from 'components/Buttons';
import { CustomIcon } from 'components/CustomIcon';
import { Typography } from 'components/Typography';
import palette from 'theme/palette';
import pxToEm from 'theme/pxToEm';
import unitsToRem from 'theme/unitsToRem';
import { useIsMobileDevice } from 'utils';
import * as Styled from './SearchAutocompleteResults.styles';
import {
  SearchAutocompleteResultsProps,
  ResultRow,
  NoResultsProps,
  ResultItemProps,
  ImageVariant,
  AutocompleteItemType,
  DidYouMeanWrapperProps,
} from './SearchAutocompleteResults.types';

const DEFAULT_SUGGESTIONS_MAX_COUNT = 5;
const DEFAULT_MOBILE_SUGGESTIONS_MAX_COUNT = 4;
const DEFAULT_MOBILE_ITEMS_MAX_COUNT = 4;

const letters = Array.from({ length: 26 }, (_, index) =>
  String.fromCharCode(97 + index),
);
const numbers = Array.from({ length: 10 }, (_, index) =>
  String.fromCharCode(48 + index),
);
const uppercaseLetters = Array.from({ length: 26 }, (_, index) =>
  String.fromCharCode(65 + index),
);
const ALLOWED_CHARACTERS = letters.concat(uppercaseLetters, numbers, [' ']);

const getAdjacentDividers = (
  ref: HTMLDivElement,
  isPreviousItemSelected: boolean,
  isNextItemSelected: boolean,
) => {
  const nextDivider = isNextItemSelected
    ? null
    : // TODO: refactor without type assertion, or add comment
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (ref.nextSibling as HTMLHRElement | null);
  const prevDivider = isPreviousItemSelected
    ? null
    : // TODO: refactor without type assertion, or add comment
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (ref.previousSibling as HTMLHRElement | null);
  return [prevDivider, nextDivider];
};

const getHighlightedText = (
  text: string,
  highlight: string,
  options: {
    inverse?: boolean;
  } = {},
) => {
  const { inverse = false } = options;

  if (!highlight) {
    return text;
  }

  const splittedHighlight = highlight.split('');
  const filteredHighlight = splittedHighlight.filter((character) =>
    ALLOWED_CHARACTERS.includes(character),
  );
  const validHighlight = filteredHighlight.join('');
  const parts = text.split(new RegExp(`(${validHighlight})`, 'gi'));

  if (parts.length <= 1) {
    return text;
  }

  return (
    <span>
      {parts.map((part: string, index: number) => {
        const isHighlighted = inverse
          ? part.toLowerCase() !== highlight.toLowerCase()
          : part.toLowerCase() === highlight.toLowerCase();

        return (
          <span
            key={index}
            style={
              isHighlighted
                ? { fontWeight: '600', color: palette.primary.main }
                : {}
            }
          >
            {part}
          </span>
        );
      })}
    </span>
  );
};

interface SleletonProps {
  isMoodboard?: boolean;
  shouldShowDividers?: boolean;
  shouldShowImages: boolean;
}
const SkeletonContainer: React.FC<SleletonProps> = ({
  isMoodboard,
  shouldShowDividers,
  shouldShowImages,
}) => {
  return (
    <Box>
      {Array.from({ length: 5 }).map((_, index) => (
        <React.Fragment key={index}>
          {shouldShowDividers && index !== 0 && <Styled.RowDivider />}
          <Styled.RowContainer isMoodboard={isMoodboard}>
            {shouldShowImages && (
              <Styled.SkeletonImageContainer
                isMoodboard={isMoodboard}
                overflow="hidden"
              >
                <Styled.SkeletonLine
                  height={isMoodboard ? unitsToRem(7) : 48}
                  width={isMoodboard ? unitsToRem(7) : 48}
                  key={index}
                ></Styled.SkeletonLine>
              </Styled.SkeletonImageContainer>
            )}

            <Box
              display="flex"
              flexDirection="column"
              rowGap={pxToEm(4)}
              flexGrow={1}
            >
              <Styled.SkeletonLine height={22} width={`${25 + index * 10}%`} />
            </Box>
            <Styled.ArrowWrapper>
              <Styled.SkeletonArrow width={24} height={24} />
            </Styled.ArrowWrapper>
          </Styled.RowContainer>
        </React.Fragment>
      ))}
    </Box>
  );
};

const SearchHistory: React.FC<Partial<SearchAutocompleteResultsProps>> = ({
  searchHistory,
  onItemClick,
  onItemHover,
  onClearHistory,
  query,
  shouldShowDividers,
  shouldShowArrows,
  shouldShowClearItemButton,
  maxHistoryItemsCount,
  maxMobileHistoryItemsCount,
  invertQueryHighlighting,
  onClearItem,
  selectedItemId,
}) => {
  const handleItemClick = (item: string) => {
    onItemClick?.(item, AutocompleteItemType.HISTORY);
  };

  const handleClearHistory = () => {
    onClearHistory?.();
  };

  const isMobileDevice = useIsMobileDevice();
  const splittedSearchHistory = React.useMemo(
    () =>
      searchHistory?.slice(
        0,
        isMobileDevice ? maxMobileHistoryItemsCount : maxHistoryItemsCount,
      ),
    [
      searchHistory,
      isMobileDevice,
      maxHistoryItemsCount,
      maxMobileHistoryItemsCount,
    ],
  );

  const iconJSX = <CustomIcon name="clock" dense />;

  return (
    <Box display="flex" flexDirection="column">
      {/*
          <Styled.SectionHeader>
            <Styled.SectionTitle>Recent Searches</Styled.SectionTitle>
            <Styled.HistoryClearButton
              label='Clear History'
              variant='text'
              size='medium'
              onClick={handleClearHistory}
            />
          </Styled.SectionHeader>
          */}
      <Box display="flex" flexDirection="column">
        {splittedSearchHistory?.map((history, index) => {
          const isSelected = history.id === selectedItemId;
          return (
            <React.Fragment key={history.value + index}>
              {shouldShowDividers && <Styled.RowDivider />}
              <RowItem
                index={index}
                type={AutocompleteItemType.HISTORY}
                key={history.id}
                item={history}
                query={query}
                onClick={handleItemClick}
                onItemHover={onItemHover}
                shoudShowImage={false}
                customIcon={iconJSX}
                shouldShowDividers={shouldShowDividers}
                shouldShowIcon
                shouldShowArrows={shouldShowArrows}
                shouldShowClearButton={shouldShowClearItemButton}
                invertQueryHighlighting={invertQueryHighlighting}
                onClear={onClearItem}
                isSelected={isSelected}
              />
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

const NoResultsAction: React.FC<Pick<NoResultsProps, 'onActionClick'>> = ({
  onActionClick,
}) => {
  return (
    <Button
      color="secondary"
      onClick={onActionClick}
      size="large"
      variant="contained"
    >
      See More Results in Catalog
    </Button>
  );
};

const NoResults: React.FC<NoResultsProps> = ({
  showAction,
  noResultsPlace,
  onActionClick,
  isMoodboard,
}) => {
  const isMobileDevice = useIsMobileDevice();

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        marginTop={isMoodboard ? '55%' : pxToEm(40)}
        marginBottom={pxToEm(40)}
        alignItems="center"
      >
        <Typography
          fontSize={isMoodboard ? unitsToRem(3) : undefined}
          color={palette.grey.A400}
          fontWeight="600"
        >
          No Results
        </Typography>
        <Typography
          fontSize={isMoodboard ? unitsToRem(2) : undefined}
          color={palette.grey.A400}
        >
          We couldn’t find anything{' '}
          {noResultsPlace ? `in "${noResultsPlace}"` : 'for your search'}
        </Typography>
      </Box>
      {showAction && !isMobileDevice && (
        <NoResultsAction onActionClick={onActionClick} />
      )}
    </Box>
  );
};

interface ImagesProps {
  images: string[];
  isMoodboard?: boolean;
}

const Images: React.FC<ImagesProps> = ({ images = [], isMoodboard }) => {
  const slicedImages = images.slice(0, 4);
  return (
    <Styled.BoardImages
      isMoodboard={isMoodboard}
      imageNumber={slicedImages?.length}
    >
      {slicedImages.map((image, index) => (
        <Styled.BoardImageGrid
          isMoodboard={isMoodboard}
          key={index}
          image={image}
        />
      ))}
      {slicedImages?.length === 0 && (
        <Styled.BoardImagePlaceholder isMoodboard={isMoodboard} />
      )}
    </Styled.BoardImages>
  );
};

const RowItem: React.FC<ResultItemProps> = ({
  item,
  imgVariant,
  query = '',
  onClick,
  shouldShowArrows,
  shoudShowImage = true,
  customIcon,
  index = 0,
  type = AutocompleteItemType.SUGGESTION,
  isMoodboard,
  isSelected = false,
  isPreviousItemSelected = false,
  isNextItemSelected = false,
  shouldShowDividers = true,
  shouldShowIcon = false,
  shouldShowClearButton = false,
  invertQueryHighlighting = false,
  onClear,
  onItemHover,
}) => {
  const isCircle = imgVariant === ImageVariant.CIRCLE;
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleItemClick = () => {
    onClick?.(item.value, type);
  };

  const handleClearClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClear?.(item.value, type);
  };

  const shouldShowPlaceholder = !item.image && shoudShowImage;
  const isImage = typeof item.image === 'string' && shoudShowImage;
  const areImages = isArray(item.image) && shoudShowImage;
  const showIcon = (shouldShowIcon || !!customIcon) && !shoudShowImage;

  const handleMouseEnter = throttle(() => {
    onItemHover?.(item.value ?? item.title ?? '', index, item);
    if (containerRef.current === null || isSelected || !shouldShowDividers) {
      return;
    }

    const dividers = getAdjacentDividers(
      containerRef.current,
      isPreviousItemSelected,
      isNextItemSelected,
    );
    dividers.forEach((divider) => {
      if (divider !== null) {
        divider.style.opacity = '0';
      }
    });
  }, 300);

  const hanldeMouseLeave = () => {
    if (containerRef.current === null || isSelected || !shouldShowDividers) {
      return;
    }
    const dividers = getAdjacentDividers(
      containerRef.current,
      isPreviousItemSelected,
      isNextItemSelected,
    );

    dividers.forEach((divider) => {
      if (divider !== null) {
        divider.style.opacity = '1';
      }
    });
  };

  const validatedLabel = (item.value ?? item.title)?.replace(
    /[^0-9a-zA-Z]+/g,
    '_',
  );
  const id = `autocomplete_${validatedLabel}_item_${type}_${
    index + 1
  }`.toLowerCase();
  const isTrending = item.type === 'trending';
  const iconName = isTrending ? 'trending' : 'search';
  const dataSelector = `search-${
    type === AutocompleteItemType.HISTORY ? 'history' : 'suggestion'
  }-drawer-link`;

  return (
    <Styled.RowContainer
      id={id}
      data-selector={dataSelector}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={hanldeMouseLeave}
      ref={containerRef}
      onClick={handleItemClick}
      isMoodboard={isMoodboard}
      isSelected={isSelected}
      withHover
    >
      {showIcon && (
        <Styled.IconContainer isMoodboard={isMoodboard}>
          {customIcon ?? <CustomIcon name={iconName} dense />}
        </Styled.IconContainer>
      )}
      {isImage && (
        <Styled.ImageContainer
          isMoodboard={isMoodboard}
          overflow="hidden"
          isCircle={isCircle}
        >
          <img
            // TODO: refactor without type assertion, or add comment
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            src={item.image as string}
            width="100%"
            height="100%"
            alt={item.title}
          />
        </Styled.ImageContainer>
      )}
      {shouldShowPlaceholder && (
        <Styled.ImageContainer isMoodboard={isMoodboard} />
      )}
      {areImages && (
        // TODO: refactor without type assertion, or add comment
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        <Images isMoodboard={isMoodboard} images={item.image as string[]} />
      )}
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        width={isMoodboard ? '50%' : undefined}
        minWidth={0}
      >
        {item.title && (
          <Typography
            fontSize={isMoodboard ? unitsToRem(2.2) : undefined}
            variant="body"
            color={palette.grey['A400']}
            width={isMoodboard ? '100%' : undefined}
            overflow={isMoodboard ? 'hidden' : undefined}
            textOverflow={isMoodboard ? 'ellipsis' : undefined}
            whiteSpace={isMoodboard ? 'nowrap' : undefined}
          >
            {getHighlightedText(item.title, query, {
              inverse: invertQueryHighlighting,
            })}
          </Typography>
        )}
        <Typography
          fontSize={isMoodboard ? unitsToRem(2) : undefined}
          variant="body"
          width={isMoodboard ? '100%' : undefined}
          overflow={isMoodboard ? 'hidden' : undefined}
          textOverflow={isMoodboard ? 'ellipsis' : undefined}
          whiteSpace={isMoodboard ? 'nowrap' : undefined}
        >
          {getHighlightedText(item.value, query, {
            inverse: invertQueryHighlighting,
          })}
        </Typography>
        {!!item.subtitle && (
          <Typography
            variant="tiny"
            color={palette.grey['A400']}
            width={isMoodboard ? '100%' : undefined}
            overflow={isMoodboard ? 'hidden' : undefined}
            textOverflow={isMoodboard ? 'ellipsis' : undefined}
            whiteSpace={isMoodboard ? 'nowrap' : undefined}
          >
            {item.subtitle}
          </Typography>
        )}
      </Box>
      {shouldShowClearButton && (
        <Styled.RowClearButton
          iconName="close"
          dense
          onClick={handleClearClick}
        />
      )}
      {shouldShowArrows && (
        <Styled.ArrowWrapper onClick={handleItemClick}>
          <CustomIcon name="arrow-right" dense />
        </Styled.ArrowWrapper>
      )}
    </Styled.RowContainer>
  );
};

const RowCategory: React.FC<ResultRow> = ({
  title,
  items,
  imgVariant,
  query,
  onItemClick,
  onItemHover,
  shouldShowArrows,
  shouldShowImages,
  isMoodboard,
  selectedItemId,
  shouldShowDividers,
  shouldShowIcons,
  maxItemsCount,
  maxMobileItemsCount,
  invertQueryHighlighting,
  isMobileDevice,
}) => {
  const splittedItems = React.useMemo(
    () => items?.slice(0, isMobileDevice ? maxMobileItemsCount : maxItemsCount),
    [items, isMobileDevice, maxItemsCount, maxMobileItemsCount],
  );

  return (
    <Box display="flex" flexDirection="column">
      {!!title && (
        <Styled.SectionHeader>
          <Styled.SectionTitle>{title}</Styled.SectionTitle>
        </Styled.SectionHeader>
      )}
      <Box>
        {splittedItems.map((item, index) => {
          const isSelected = item.id === selectedItemId;
          const isPreviousItemSelected =
            splittedItems[index - 1]?.id === selectedItemId;
          const isNextItemSelected =
            splittedItems[index + 1]?.id === selectedItemId;
          const shouldHideDivider =
            (index === 0 && (!title || title.length < 0)) ||
            isSelected ||
            isPreviousItemSelected;

          return (
            <React.Fragment key={index + item.value}>
              {shouldShowDividers && (
                <Styled.RowDivider shouldHide={shouldHideDivider} />
              )}
              <RowItem
                index={index}
                type={AutocompleteItemType.SUGGESTION}
                shouldShowArrows={shouldShowArrows}
                item={item}
                imgVariant={imgVariant}
                query={query}
                onClick={onItemClick}
                onItemHover={onItemHover}
                shoudShowImage={shouldShowImages}
                isMoodboard={isMoodboard}
                isSelected={isSelected}
                isPreviousItemSelected={isPreviousItemSelected}
                isNextItemSelected={isNextItemSelected}
                shouldShowDividers={shouldShowDividers}
                shouldShowIcon={shouldShowIcons}
                invertQueryHighlighting={invertQueryHighlighting}
              />
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

const DidYouMeanWrapper: React.FC<DidYouMeanWrapperProps> = ({
  query,
  correctedQuery,
  onQueryClick,
  isMoodboard,
}) => {
  return (
    <Styled.CorrectedQueryWrapper isMoodboard={isMoodboard}>
      <Box display="flex" flexDirection="column">
        <Styled.CorrectionLine variant="body" mb="0.6em">
          <strong className="resultsLabel">Show Results for</strong>
          <Styled.CorrectionLink
            dense
            underlined
            variant="text"
            color="inherit"
            onClick={() => onQueryClick?.(correctedQuery)}
          >
            {correctedQuery?.trim()}
          </Styled.CorrectionLink>
        </Styled.CorrectionLine>
        <Styled.CorrectionLine variant="tiny">
          <span className="resultsLabel">Search instead for</span>
          <Styled.CorrectionLink
            dense
            underlined
            variant="text"
            color="inherit"
            onClick={() => onQueryClick?.(query)}
          >
            {query?.trim()}
          </Styled.CorrectionLink>
        </Styled.CorrectionLine>
      </Box>
    </Styled.CorrectedQueryWrapper>
  );
};

const SearchAutocompleteResults: React.FC<SearchAutocompleteResultsProps> = ({
  results,
  isResultsOpen = false,
  imgVariant = ImageVariant.SQUARE,
  query = '',
  correctedQuery = '',
  showAction = false,
  shouldShowHistory = false,
  shouldShowSuggestions = false,
  searchHistory,
  onItemClick,
  onClearHistory,
  isLoading = false,
  shouldShowArrows = false,
  shouldShowImages = false,
  onActionClick,
  onQueryClick,
  withRowGap = true,
  noResultsPlace,
  isMoodboard = false,
  selectedItemId = null,
  shouldShowDividers = true,
  shouldShowIcons = false,
  shouldShowClearItemButton = false,
  maxItemsCount,
  maxMobileItemsCount = maxItemsCount ?? DEFAULT_MOBILE_ITEMS_MAX_COUNT,
  maxHistoryItemsCount = maxItemsCount ?? DEFAULT_SUGGESTIONS_MAX_COUNT,
  maxMobileHistoryItemsCount = maxMobileItemsCount ??
    DEFAULT_MOBILE_SUGGESTIONS_MAX_COUNT,
  invertQueryHighlighting = false,
  className,
  onClearItem,
  onItemHover,
}) => {
  if (!isResultsOpen) return null;

  const isMobileDevice = useIsMobileDevice();

  const gap = withRowGap ? 24 : 0;

  return (
    <Box
      id="searchAutocompleteResults"
      display="flex"
      flexDirection="column"
      rowGap={pxToEm(gap)}
      marginY={pxToEm(40)}
      className={className}
    >
      {isLoading && (
        <SkeletonContainer
          isMoodboard={isMoodboard}
          shouldShowDividers={shouldShowDividers}
          shouldShowImages={shouldShowImages}
        />
      )}
      {shouldShowHistory && !isLoading && (
        <SearchHistory
          searchHistory={searchHistory}
          onItemClick={onItemClick}
          onItemHover={onItemHover}
          onClearHistory={onClearHistory}
          query={query}
          isMoodboard={isMoodboard}
          shouldShowDividers={shouldShowDividers}
          shouldShowArrows={shouldShowArrows}
          shouldShowClearItemButton={shouldShowClearItemButton}
          maxHistoryItemsCount={maxHistoryItemsCount}
          maxMobileHistoryItemsCount={maxMobileHistoryItemsCount}
          invertQueryHighlighting={invertQueryHighlighting}
          onClearItem={onClearItem}
          selectedItemId={selectedItemId}
        />
      )}
      {shouldShowSuggestions && !isLoading && (
        <>
          {correctedQuery?.length > 0 && (
            <DidYouMeanWrapper
              query={query}
              correctedQuery={correctedQuery}
              onQueryClick={(val) => onQueryClick?.(val)}
              isMoodboard={isMoodboard}
            />
          )}
          {results.map(({ title, items }, i) => (
            <RowCategory
              shouldShowArrows={shouldShowArrows}
              key={i}
              title={title}
              items={items}
              imgVariant={imgVariant}
              query={query}
              onItemClick={onItemClick}
              onItemHover={onItemHover}
              shouldShowImages={shouldShowImages}
              isMoodboard={isMoodboard}
              selectedItemId={selectedItemId}
              shouldShowDividers={shouldShowDividers}
              shouldShowIcons={shouldShowIcons}
              maxItemsCount={maxItemsCount}
              maxMobileItemsCount={maxMobileItemsCount}
              invertQueryHighlighting={invertQueryHighlighting}
              isMobileDevice={isMobileDevice}
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default SearchAutocompleteResults;
